import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    alpha,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Fab,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MdClose, MdOutlineInsights } from 'react-icons/md';
import { SiOpenai } from 'react-icons/si';
import { AppVarsContext } from '../../../context/AppVarsContext';
import { getCookie } from '../../../dwi-functions';
import { consumeGa4AuditStream, ga4AuditErrorMessage } from './ga4AuditStream';

const SAMPLE_PROMPTS = {
    overview: [
        'Which platform looks least efficient this month, and what should I change first?',
        'What is driving the strongest return across my paid channels right now?',
        'Where are conversions being generated without enough supporting click volume?',
        'If I had to make one budget move today across platforms, what should it be?'
    ],
    google_paid: [
        'Which campaigns are carrying revenue and which ones are wasting spend?',
        'What should I do next based on the current Google Paid data?',
        'Which search terms or keywords deserve expansion versus cleanup?',
        'Where is spend outrunning conversion value in this Google Paid report?'
    ],
    google_grant: [
        'Which grant campaigns are creating value and which ones need attention?',
        'What is the biggest efficiency issue in this Google Grant report?',
        'Which campaigns should I tighten first to improve grant efficiency?',
        'What part of this grant report looks most under-optimized right now?'
    ],
    meta: [
        'Which audience or campaign deserves more budget based on this Meta report?',
        'What is the clearest optimization move from this Meta data?',
        'Which audience segment is underperforming relative to spend?',
        'What is the strongest creative or campaign signal in this Meta report?'
    ],
    bing: [
        'Which Bing campaigns or queries deserve action right now?',
        'Where is Bing losing efficiency in this date range?',
        'Which search terms look strongest versus weakest in Bing?',
        'What is the clearest Bing budget or bid adjustment to make next?'
    ],
    nextdoor: [
        'Which Nextdoor campaigns deserve more budget based on spend efficiency?',
        'What is the clearest optimization move in this Nextdoor report?',
        'Which Nextdoor ads are carrying the strongest click or conversion signal?',
        'Where is Nextdoor spend failing to turn into enough response volume?'
    ],
    display: [
        'Which Display channels or campaigns are strongest right now?',
        'What creative or channel issue stands out in this Display report?',
        'Which Display channel deserves more budget and which one needs scrutiny?',
        'What is the clearest signal about Programmatic performance in this date range?'
    ],
    instant_analysis: [
        'What is the most important journey signal to act on first?',
        'What is the clearest cross-channel recommendation from this report?',
        'Where does this journey data show friction before conversion?',
        'What campaign or source question should I investigate next from this report?'
    ]
};

const parseSettings = (value) => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
};

const normalizeSections = (sections) => (
    Array.isArray(sections)
        ? sections.map((section) => ({
            title: section?.title || section?.heading || 'Section',
            bullets: Array.isArray(section?.bullets)
                ? section.bullets
                : Array.isArray(section?.items)
                    ? section.items
                    : []
        }))
        : []
);

const normalizeTable = (table) => {
    if (!table || typeof table !== 'object') return null;

    const columns = Array.isArray(table?.columns) ? table.columns : [];
    const rows = Array.isArray(table?.rows) ? table.rows : [];

    return {
        title: table?.title || '',
        columns,
        rows: rows.map((row) => {
            if (Array.isArray(row)) return row;
            if (row && typeof row === 'object') {
                return columns.map((column) => row?.[column] ?? '');
            }
            return [];
        }).filter((row) => row.length > 0)
    };
};

const normalizeHighlights = (highlights) => (
    Array.isArray(highlights)
        ? highlights.map((highlight) => ({
            label: highlight?.label || '',
            value: highlight?.value || '',
            note: highlight?.note || ''
        }))
        : []
);

const normalizeAnswer = (payload) => ({
    title: payload?.title || 'Report answer',
    direct_answer: payload?.direct_answer || 'No direct answer returned.',
    summary: payload?.summary || '',
    highlights: normalizeHighlights(payload?.highlights),
    sections: normalizeSections(payload?.sections),
    table: normalizeTable(payload?.table),
    follow_up: payload?.follow_up || '',
    report_shape: payload?.report_shape || ''
});

const ReportQuestionAssistant = ({
    enabled,
    reportType,
    reportTitle,
    client,
    profile,
    contextPayload = {},
    ga4Audit = null,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { appVars } = useContext(AppVarsContext);
    const [open, setOpen] = useState(false);
    const [question, setQuestion] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [history, setHistory] = useState([]);
    const contentRef = useRef(null);
    const auditStartedRef = useRef(false);

    const settings = useMemo(() => parseSettings(profile?.settings), [profile?.settings]);
    const derivedEnabled = enabled ?? (settings?.ad_sets?.report_question_assistant?.id === 1);

    const clientId = contextPayload?.cid
        || client?.id
        || client?.cid
        || profile?.cid
        || profile?.clientid
        || sessionStorage.getItem('cid')
        || sessionStorage.getItem('rid');

    const sdate = sessionStorage.getItem('sDate');
    const edate = sessionStorage.getItem('eDate');
    const prompts = SAMPLE_PROMPTS[reportType] || [
        'What is the most important performance insight in this report?',
        'What should I change first based on the current data?'
    ];

    useEffect(() => {
        if (!open || !contentRef.current) return;
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }, [history, open]);

    const handleAsk = useCallback(async (nextQuestion) => {
        const trimmed = String(nextQuestion || question || '').trim();
        if (!trimmed || !appVars?.api_url || !clientId || !reportType) return;

        setSubmitting(true);
        setError(null);

        const optimisticId = Date.now();
        setHistory((prev) => ([
            ...prev,
            {
                id: optimisticId,
                question: trimmed,
                answer: null,
                loading: true
            }
        ]));

        try {
            const response = await fetch(`${appVars.api_url}/report-question`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    cid: clientId,
                    sdate,
                    edate,
                    question: trimmed,
                    report_type: reportType,
                    context: {
                        reportTitle,
                        ...contextPayload,
                    }
                })
            });

            const payload = await response.json().catch(() => null);
            if (!response.ok) {
                const responseError = new Error(payload?.error || 'The report assistant could not answer this question.');
                responseError.retryable = payload?.retryable !== false;
                responseError.retryAfterSeconds = Number(payload?.retry_after_seconds || 0);
                throw responseError;
            }

            setHistory((prev) => prev.map((entry) => (
                entry.id === optimisticId
                    ? {
                        ...entry,
                        loading: false,
                        answer: normalizeAnswer(payload?.answer)
                    }
                    : entry
            )));
            setQuestion('');
        } catch (requestError) {
            setHistory((prev) => prev.filter((entry) => entry.id !== optimisticId));
            setError({
                message: requestError?.message || 'The report assistant could not answer this question. Please try again.',
                retryable: requestError?.retryable !== false,
                retryAfterSeconds: Number(requestError?.retryAfterSeconds || 0),
                question: trimmed,
            });
        } finally {
            setSubmitting(false);
        }
    }, [appVars?.api_url, clientId, contextPayload, edate, question, reportTitle, reportType, sdate]);

    const handleRunGa4Audit = useCallback(async () => {
        if (!appVars?.api_url || !ga4Audit?.propertyId || submitting) return;
        const optimisticId = Date.now();
        auditStartedRef.current = true;
        setOpen(true);
        setSubmitting(true);
        setError(null);
        setHistory((previous) => [...previous, {
            id: optimisticId,
            question: 'Run GA4 Audit',
            loading: true,
            streamText: '',
            activity: 'Analyzing GA4 traffic and tracking…',
        }]);
        try {
            const response = await fetch(`${appVars.api_url}/integrations/ga4/audit/stream`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-DW-User-Id': getCookie('_au') || '',
                },
                body: JSON.stringify({
                    propertyId: ga4Audit.propertyId,
                    accountId: ga4Audit.accountId || clientId,
                    dateRange: { startDate: sdate, endDate: edate },
                    prompt: 'Run a complete GA4 analytics and tracking audit for the selected report period.',
                }),
            });
            await consumeGa4AuditStream(response, ({ event, data }) => {
                if (event === 'audit.error') throw Object.assign(new Error(data?.message), { code: data?.code });
                setHistory((previous) => previous.map((entry) => entry.id !== optimisticId ? entry : {
                    ...entry,
                    auditId: data?.auditId || entry.auditId,
                    streamText: event === 'audit.delta' ? `${entry.streamText || ''}${data?.delta || ''}` : entry.streamText,
                    activity: event === 'audit.tool.started'
                        ? `Checking ${(data?.label || data?.tool || 'GA4 data').replaceAll('_', ' ')}…`
                        : event === 'audit.completed' ? 'Audit complete' : entry.activity,
                    loading: event === 'audit.completed' ? false : entry.loading,
                }));
            });
        } catch (auditError) {
            setHistory((previous) => previous.map((entry) => entry.id === optimisticId ? { ...entry, loading: false, failed: true } : entry));
            setError({ message: ga4AuditErrorMessage(auditError?.code), retryable: auditError?.code !== 'GA4_PERMISSION_DENIED', audit: true });
        } finally {
            setSubmitting(false);
        }
    }, [appVars?.api_url, clientId, edate, ga4Audit, sdate, submitting]);

    const handleOpen = useCallback(() => {
        setOpen(true);
        if (ga4Audit && !auditStartedRef.current) handleRunGa4Audit();
    }, [ga4Audit, handleRunGa4Audit]);

    const canRender = derivedEnabled && Boolean(reportType) && Boolean(clientId);
    const helperChips = useMemo(() => ([
        reportTitle ? { label: reportTitle } : null,
        sdate && edate ? { label: `${sdate} to ${edate}` } : null,
        reportType ? { label: reportType.replace(/_/g, ' ') } : null,
    ].filter(Boolean)), [edate, reportTitle, reportType, sdate]);

    if (!canRender) return null;

    return (
        <>
            <Tooltip title={ga4Audit ? 'Run a streamed GA4 audit' : 'Ask about this report'}>
                <Fab
                    color="primary"
                    onClick={handleOpen}
                    sx={{
                        position: 'fixed',
                        right: { xs: 16, md: 28 },
                        bottom: { xs: 20, md: 28 },
                        zIndex: theme.zIndex.modal - 1,
                        borderRadius: 999,
                        px: 2,
                        gap: 1,
                        width: 'auto',
                        minHeight: 56,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.92)} 0%, ${alpha(theme.palette.secondary.main, 0.92)} 100%)`,
                        boxShadow: `0 24px 54px ${alpha(theme.palette.primary.main, 0.32)}`,
                    }}
                >
                    <SiOpenai size={18} />
                    {!isMobile ? <Typography sx={{ fontWeight: 700 }}>{ga4Audit ? 'Run GA4 Audit' : 'Ask report'}</Typography> : null}
                </Fab>
            </Tooltip>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="lg"
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 4,
                        overflow: 'hidden',
                        background: alpha('#ffffff', 0.88),
                        backdropFilter: 'blur(18px)',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                        boxShadow: `0 28px 80px ${alpha(theme.palette.primary.main, 0.24)}`,
                    }
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: alpha('#0f172a', 0.62),
                        backdropFilter: 'blur(8px)'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)} 0%, ${alpha(theme.palette.secondary.main, 0.14)} 100%)`,
                    }}
                >
                    <Stack spacing={0.75}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip
                                icon={<MdOutlineInsights size={16} />}
                                label={ga4Audit ? 'GA4 Audit · Report Assistant' : 'Report Question Assistant'}
                                sx={{
                                    bgcolor: alpha('#ffffff', 0.82),
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                                    fontWeight: 700,
                                }}
                            />
                            {helperChips.map((chip) => (
                                <Chip key={chip.label} label={chip.label} size="small" sx={{ bgcolor: alpha('#ffffff', 0.72) }} />
                            ))}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            {ga4Audit ? 'Review tracking quality, acquisition, engagement, and conversions as findings stream in.' : 'Ask a performance question and the API will gather the report data for this client before answering.'}
                        </Typography>
                    </Stack>
                    <IconButton onClick={() => setOpen(false)}>
                        <MdClose />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '280px minmax(0, 1fr)' },
                        gap: 2,
                        p: 0,
                    }}
                >
                    <Box
                        sx={{
                            p: 2,
                            borderRight: { md: `1px solid ${alpha(theme.palette.primary.main, 0.12)}` },
                            background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha('#ffffff', 0.8)} 100%)`,
                        }}
                    >
                        <Typography variant="overline" color="text.secondary">
                            Good prompts
                        </Typography>
                        <Stack spacing={1} sx={{ mt: 1.25 }}>
                            {prompts.map((prompt) => (
                                <Button
                                    key={prompt}
                                    onClick={() => handleAsk(prompt)}
                                    disabled={submitting}
                                    variant="outlined"
                                    sx={{
                                        justifyContent: 'flex-start',
                                        textAlign: 'left',
                                        borderRadius: 3,
                                        borderColor: alpha(theme.palette.primary.main, 0.16),
                                        background: alpha('#ffffff', 0.72),
                                    }}
                                >
                                    {prompt}
                                </Button>
                            ))}
                        </Stack>
                    </Box>

                    <Box sx={{ minWidth: 0, display: 'grid', gridTemplateRows: '1fr auto' }}>
                        <Box
                            ref={contentRef}
                            sx={{
                                minHeight: 360,
                                maxHeight: { xs: 'none', md: '65vh' },
                                overflowY: 'auto',
                                p: 2,
                                display: 'grid',
                                gap: 1.5,
                                alignContent: 'start'
                            }}
                        >
                            {!history.length ? (
                                <Card
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 3,
                                        borderColor: alpha(theme.palette.primary.main, 0.16),
                                        background: alpha('#ffffff', 0.82)
                                    }}
                                >
                                    <CardContent sx={{ display: 'grid', gap: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                            Ask a question about this report
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            The assistant will use the current client, date range, and report context. For filtered reports like Google Paid, it can also use the active network and conversion filters.
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ) : null}

                            {history.map((entry) => (
                                <Box key={entry.id} sx={{ display: 'grid', gap: 1 }}>
                                    <Box
                                        sx={{
                                            justifySelf: 'flex-end',
                                            maxWidth: '90%',
                                            px: 1.5,
                                            py: 1.15,
                                            borderRadius: '18px 18px 6px 18px',
                                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`
                                        }}
                                    >
                                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{entry.question}</Typography>
                                    </Box>

                                    <Card
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 3,
                                            borderColor: alpha(theme.palette.secondary.main, 0.16),
                                            background: alpha('#ffffff', 0.88)
                                        }}
                                    >
                                        <CardContent sx={{ display: 'grid', gap: 1.5 }}>
                                            {entry.streamText ? (
                                                <Stack spacing={1.25}>
                                                    {entry.loading ? (
                                                        <Stack direction="row" spacing={1.25} alignItems="center">
                                                            <CircularProgress size={18} />
                                                            <Typography variant="body2" color="text.secondary">{entry.activity}</Typography>
                                                        </Stack>
                                                    ) : null}
                                                    <Typography component="div" variant="body1" sx={{ lineHeight: 1.75, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                                        {entry.streamText}
                                                    </Typography>
                                                </Stack>
                                            ) : entry.loading ? (
                                                <Stack direction="row" spacing={1.25} alignItems="center">
                                                    <CircularProgress size={18} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {entry.activity || 'Building an answer from the current report data.'}
                                                    </Typography>
                                                </Stack>
                                            ) : entry.answer ? (
                                                <>
                                                    <Box>
                                                        <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                                                            {entry.answer.title}
                                                        </Typography>
                                                        {entry.answer.summary ? (
                                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                {entry.answer.summary}
                                                            </Typography>
                                                        ) : null}
                                                    </Box>

                                                    <Typography variant="body1" sx={{ lineHeight: 1.75 }}>
                                                        {entry.answer.direct_answer}
                                                    </Typography>

                                                    {entry.answer.highlights.length ? (
                                                        <Box
                                                            sx={{
                                                                display: 'grid',
                                                                gap: 1,
                                                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }
                                                            }}
                                                        >
                                                            {entry.answer.highlights.map((highlight, index) => (
                                                                <Card
                                                                    key={`${entry.id}-highlight-${index}`}
                                                                    variant="outlined"
                                                                    sx={{
                                                                        borderRadius: 3,
                                                                        borderColor: alpha(theme.palette.primary.main, 0.14),
                                                                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha('#ffffff', 0.92)} 100%)`
                                                                    }}
                                                                >
                                                                    <CardContent sx={{ display: 'grid', gap: 0.35 }}>
                                                                        <Typography variant="overline" color="text.secondary">
                                                                            {highlight.label}
                                                                        </Typography>
                                                                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                                                            {highlight.value}
                                                                        </Typography>
                                                                        {highlight.note ? (
                                                                            <Typography variant="body2" color="text.secondary">
                                                                                {highlight.note}
                                                                            </Typography>
                                                                        ) : null}
                                                                    </CardContent>
                                                                </Card>
                                                            ))}
                                                        </Box>
                                                    ) : null}

                                                    {entry.answer.sections.length ? (
                                                        <Stack spacing={1.25}>
                                                            {entry.answer.sections.map((section, index) => (
                                                                <Card
                                                                    key={`${entry.id}-section-${index}`}
                                                                    variant="outlined"
                                                                    sx={{
                                                                        borderRadius: 3,
                                                                        borderColor: alpha(theme.palette.primary.main, 0.12)
                                                                    }}
                                                                >
                                                                    <CardContent sx={{ display: 'grid', gap: 0.85 }}>
                                                                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                                                            {section.title}
                                                                        </Typography>
                                                                        <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 0.65 }}>
                                                                            {section.bullets.map((bullet, bulletIndex) => (
                                                                                <Typography component="li" key={`${entry.id}-section-${index}-${bulletIndex}`} variant="body2">
                                                                                    {bullet}
                                                                                </Typography>
                                                                            ))}
                                                                        </Box>
                                                                    </CardContent>
                                                                </Card>
                                                            ))}
                                                        </Stack>
                                                    ) : null}

                                                    {entry.answer.table?.columns?.length ? (
                                                        <TableContainer
                                                            sx={{
                                                                borderRadius: 3,
                                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                                                overflow: 'hidden'
                                                            }}
                                                        >
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                                                                        {entry.answer.table.columns.map((column) => (
                                                                            <TableCell key={`${entry.id}-column-${column}`} sx={{ fontWeight: 800 }}>
                                                                                {column}
                                                                            </TableCell>
                                                                        ))}
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {entry.answer.table.rows.map((row, rowIndex) => (
                                                                        <TableRow key={`${entry.id}-row-${rowIndex}`}>
                                                                            {row.map((value, cellIndex) => (
                                                                                <TableCell key={`${entry.id}-row-${rowIndex}-cell-${cellIndex}`}>
                                                                                    {value}
                                                                                </TableCell>
                                                                            ))}
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    ) : null}

                                                    {entry.answer.follow_up ? (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Next question: {entry.answer.follow_up}
                                                        </Typography>
                                                    ) : null}
                                                </>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    No answer returned.
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Box>
                            ))}
                        </Box>

                        <Box
                            sx={{
                                p: 2,
                                borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                background: alpha('#ffffff', 0.92),
                                display: 'grid',
                                gap: 1
                            }}
                        >
                            {error ? (
                                <Alert
                                    severity={error.retryable ? 'warning' : 'error'}
                                    variant="outlined"
                                    action={error.retryable ? (
                                        <Button
                                            color="inherit"
                                            size="small"
                                            onClick={() => error.audit ? handleRunGa4Audit() : handleAsk(error.question)}
                                            disabled={submitting}
                                        >
                                            Try again
                                        </Button>
                                    ) : null}
                                >
                                    {error.message}
                                    {error.retryable && error.retryAfterSeconds ? ` Try again in about ${error.retryAfterSeconds} seconds.` : ''}
                                </Alert>
                            ) : null}

                            <TextField
                                multiline
                                minRows={3}
                                maxRows={6}
                                value={question}
                                onChange={(event) => setQuestion(event.target.value)}
                                placeholder="Ask about efficiency, pacing, campaign quality, channel mix, or what to do next."
                                fullWidth
                                onKeyDown={(event) => {
                                    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                                        event.preventDefault();
                                        handleAsk();
                                    }
                                }}
                            />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Typography variant="caption" color="text.secondary">
                                    Uses the current client and date range. Press Cmd/Ctrl + Enter to submit.
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={() => handleAsk()}
                                    disabled={submitting || !question.trim()}
                                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SiOpenai size={14} />}
                                    sx={{ minWidth: 140 }}
                                >
                                    {submitting ? 'Thinking...' : 'Ask report'}
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ReportQuestionAssistant;
